import { ScrollReveal } from "./ScrollReveal";
import { Play } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const faqs = [
  {
    q: "What is PRObroker and how can I find owner properties?",
    a: [
      "PRObroker is a smart and easy-to-use real estate platform designed to help you find verified owner properties directly—without middlemen.",
      "You can use PRObroker seamlessly on your computer or mobile phone.",
      "👉 Watch our tutorial video to understand all the features and learn how to search for owner properties step-by-step.",
    ],
    videoUrl: "https://www.youtube.com/embed/Y8PNGzttL-U?si=04l4Q4nfyDo2uFZm",
  },
  {
    q: "How can I list my property as a real estate agent or builder?",
    a: [
      "Listing your property on PRObroker is quick and simple!",
      "Just follow these 3 easy steps:",
      "Go to the “Add Property” / “List Property” section.",
      "Fill in your property or project details.",
      "Upload photos, videos, or a brochure, and click Submit.",
      "Your listing will go live instantly.",
      "👉 For a detailed walkthrough, watch this video.",
    ],
    videoUrl: "https://www.youtube.com/embed/zreyxicVMso?si=P6HwgVn3W7JRwOcz",
  },
  {
    q: "How do I find broker properties?",
    a: [
      "If you have specific property requirements and can't find matches in Owner Properties, simply switch to the Broker Properties section.",
      "Use the available filters to see all matching results as per your needs.",
      "💡 Tip: You can also use the same process in reverse—find client requirements if you have properties to offer.",
      "👉 For more details, watch this video tutorial.",
    ],
    videoUrl: "https://www.youtube.com/embed/zreyxicVMso?si=eXDva2bO4XdYRWE4",
  },
  {
    q: "Does PRObroker charge any commission on property deals?",
    a: [
      "No, PRObroker does not charge any commission — neither from owners nor from brokers.",
      "We simply connect you directly, without any middle cost.",
    ],
  },
  {
    q: "In which cities or areas is PRObroker currently available?",
    a: [
      "PRObroker is currently active in West Ahmedabad, covering 70+ local areas.",
      "You can view the full list of available areas in the Filter Section of the platform.",
    ],
  },
];

const FAQSection = () => (
  <section className="section-white py-20 sm:py-28">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <ScrollReveal>
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground text-center mb-3">
            Support
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4 text-foreground">
            Frequently Asked{" "}
            <span className="gradient-text font-extrabold">Questions</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-md mx-auto text-sm leading-relaxed">
            Everything you need to know about PRObroker. Can't find an answer?
            Reach out to our team.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-xl px-5 sm:px-6 bg-background hover:border-primary/15 transition-colors data-[state=open]:border-primary/20 data-[state=open]:shadow-sm"
              >
                <AccordionTrigger className="text-sm sm:text-[15px] font-medium text-foreground hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <ul className="space-y-2">
                    {faq.a.map((point, index) => (
                      <li key={index} className="flex items-start">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-600 mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-sm text-muted-foreground leading-relaxed">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {faq.videoUrl && (
                    <div className="mt-5">
                      <div className="relative rounded-lg overflow-hidden shadow-lg bg-gray-900 aspect-video max-w-md mx-auto">
                        <iframe
                          className="w-full h-full"
                          src={faq.videoUrl}
                          title={faq.q}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                        <Play className="w-3.5 h-3.5" />
                        Watch our video guide
                      </p>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollReveal>
      </div>
    </div>
  </section>
);

export default FAQSection;